/**
 * Sales Pipeline - SharePoint analytics strip.
 *
 * Spec v2: shows the *true* win/loss/cancellation picture that the
 * existing Cognito-driven funnel can't (lost rows are deleted from
 * Cognito; this reads from the SharePoint "Medicys Schedule of Work"
 * master tracker via a 15-min Mongo cache).
 *
 * Layout:
 *   1. Headline KPI row (Opps · Won · Lost · Cancelled · Win Rate)
 *   2. Compact 9-stage horizontal funnel strip with opp counts
 *   3. Loss-reason breakdown (top 5 + others)
 *   4. Cancellation post-mortem widget (most recent decisions with
 *      free-text reasons)
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtCurrencyShort = (v) => {
  if (v == null) return "-";
  if (Math.abs(v) >= 1_000_000) return `£${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `£${Math.round(v / 1_000).toLocaleString("en-GB")}k`;
  return `£${Math.round(v).toLocaleString("en-GB")}`;
};

const fmtPct = (v) => {
  if (v == null) return "-";
  return `${(v * 100).toFixed(1)}%`;
};


const STAGE_TONE = {
  quoted:       "bg-sky-50 border-sky-200 text-sky-900",
  commissioned: "bg-emerald-50 border-emerald-200 text-emerald-900",
  sow_signed:   "bg-emerald-50 border-emerald-200 text-emerald-900",
  briefed:      "bg-emerald-50 border-emerald-200 text-emerald-900",
  in_field:     "bg-emerald-50 border-emerald-200 text-emerald-900",
  finished:     "bg-emerald-50 border-emerald-200 text-emerald-900",
  invoiced:     "bg-emerald-100 border-emerald-300 text-emerald-900",
  lost:         "bg-rose-50 border-rose-200 text-rose-900",
  cancelled:    "bg-stone-100 border-stone-300 text-stone-700",
};

export default function PipelineAnalytics({ activeBook = "quoted", onSelectBook }) {
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastSync, setLastSync] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: f } = await axios.get(
        `${API}/api/proposals/pipeline/sharepoint?rows_per_stage=0`
      );
      setFunnel(f);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load SharePoint analytics.");
    } finally {
      setLoading(false);
    }
    try {
      const { data: s } = await axios.get(
        `${API}/api/proposals/pipeline/sharepoint/status`
      );
      setLastSync(s?.last_sync?.completed_at || null);
    } catch {
      /* indicator only */
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => {
      if (!document.hidden) load();
    }, 15 * 60 * 1000);
    return () => clearInterval(id);
  }, [load]);

  if (loading) {
    return (
      <div
        data-testid="pipeline-analytics-loading"
        className="flex items-center gap-2 text-stone-500 text-sm py-6"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading opportunity analytics from SharePoint…
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="pipeline-analytics-error"
        className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2"
      >
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>{error}</span>
      </div>
    );
  }

  if (!funnel) return null;
  const t = funnel.totals || {};
  const won = t.won ?? 0;
  const lost = t.lost ?? 0;
  const cancelled = t.cancelled ?? 0;
  const decided = won + lost + cancelled;
  const salesWinRate = decided ? (won + cancelled) / decided : null;
  const realisedWinRate = decided ? won / decided : null;
  const n = (v) => v.toLocaleString("en-GB");

  return (
    <section
      data-testid="pipeline-analytics"
      className="bg-white border border-stone-200 rounded-2xl overflow-hidden shadow-sm"
    >
      {/* ---- top bar: title + sync ---- */}
      <header className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-stone-100">
        <div>
          <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-[#1B4F8A] mb-0.5">
            Opportunity analytics - SharePoint
          </p>
          <p className="text-sm text-stone-600">
            True win/loss across <span className="font-medium text-slate-800">{t.opportunities?.toLocaleString() ?? 0}</span> opportunities (includes deals removed from the legacy CRM).
          </p>
        </div>
        <div
          data-testid="pipeline-sync-indicator"
          className="flex items-center gap-2 text-[11px] text-stone-400 whitespace-nowrap"
          title="SharePoint is re-synced and the likelihood model retrained automatically every 15 minutes, around the clock."
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          live · syncs every 15 min
          {lastSync &&
            ` · synced ${new Date(lastSync).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} · model retrained`}
        </div>
      </header>

      {/* ---- KPI row ---- */}
      <TooltipProvider delayDuration={150}>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-stone-100">
          <Kpi
            label="Opportunities"
            value={t.opportunities?.toLocaleString() ?? "-"}
            testid="pipeline-kpi-opportunities"
            tooltip="Every distinct opportunity in the SharePoint Schedule of Work — live, won, lost and cancelled. Sibling quotes for the same project count once."
          />
          <Kpi
            label="Won"
            value={t.won?.toLocaleString() ?? "-"}
            sublabel={
              t.won != null && t.opportunities
                ? `${((t.won / t.opportunities) * 100).toFixed(1)}% of all opportunities`
                : null
            }
            tone="emerald"
            testid="pipeline-kpi-won"
            tooltip={`Deals that reached Commissioned or beyond (SOW Signed, Briefed, In Field, Finished, Invoiced) and stuck. ${n(won)} ÷ ${n(t.opportunities ?? 0)} opportunities = ${t.opportunities ? ((won / t.opportunities) * 100).toFixed(1) : "-"}%.`}
          />
          <Kpi
            label="Lost"
            value={t.lost?.toLocaleString() ?? "-"}
            sublabel={
              t.lost != null && t.opportunities
                ? `${((t.lost / t.opportunities) * 100).toFixed(1)}% of all opportunities`
                : null
            }
            tone="rose"
            testid="pipeline-kpi-lost"
            tooltip={`Quotes where the sale never landed — the client went elsewhere or the brief died. ${n(lost)} ÷ ${n(t.opportunities ?? 0)} opportunities = ${t.opportunities ? ((lost / t.opportunities) * 100).toFixed(1) : "-"}%.`}
          />
          <Kpi
            label="Cancelled"
            value={t.cancelled?.toLocaleString() ?? "-"}
            sublabel={
              t.cancelled != null && won + cancelled > 0
                ? `${((cancelled / (won + cancelled)) * 100).toFixed(1)}% of won work`
                : null
            }
            tone="stone"
            testid="pipeline-kpi-cancelled"
            tooltip={`Deals we won (commissioned) that were later cancelled before delivery — post-sale attrition, not a lost pitch. ${n(cancelled)} ÷ (${n(won)} won + ${n(cancelled)} cancelled) = ${won + cancelled ? ((cancelled / (won + cancelled)) * 100).toFixed(1) : "-"}% of won work.`}
          />
          <Kpi
            label="Sales win rate"
            value={fmtPct(salesWinRate)}
            sublabel={realisedWinRate != null ? `realised ${fmtPct(realisedWinRate)}` : null}
            tone="navy"
            testid="pipeline-kpi-win-rate"
            tooltip={`Sales win rate counts cancelled as won — the sale succeeded, the project fell through later. (${n(won)} won + ${n(cancelled)} cancelled) ÷ ${n(decided)} decided = ${fmtPct(salesWinRate)}. Realised win rate counts only deals that stuck: ${n(won)} ÷ ${n(decided)} = ${fmtPct(realisedWinRate)}.`}
          />
        </div>
      </TooltipProvider>

      {/* ---- mini funnel strip ---- */}
      <div className="px-5 py-4 border-t border-stone-100">
        <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-stone-500 mb-2">
          Funnel by status (opportunities)
        </p>
        <div
          data-testid="pipeline-mini-funnel"
          className="flex flex-wrap gap-2"
        >
          {(funnel.stages || []).map((s) => {
            const BOOK_STAGES = {
              quoted: {
                book: "quoted",
                hover: "hover:border-sky-400",
                ring: "ring-sky-300",
                title: "View the quoted book (live opportunities, scored)",
              },
              commissioned: {
                book: "commissioned",
                hover: "hover:border-emerald-400",
                ring: "ring-emerald-300",
                title: "View the commissioned book (won work: teams, time-to-win, clients)",
              },
              sow_signed: {
                book: "sow",
                hover: "hover:border-violet-400",
                ring: "ring-violet-300",
                title: "View the SOW book (signed work: quote → commission → SOW timing)",
              },
              briefed: {
                book: "briefed",
                hover: "hover:border-indigo-400",
                ring: "ring-indigo-300",
                title: "View the briefed book (quote → commission → SOW → brief timing)",
              },
              in_field: {
                book: "infield",
                hover: "hover:border-amber-400",
                ring: "ring-amber-300",
                title: "View the in-field book (quote → commission → SOW → brief → field timing)",
              },
              invoiced: {
                book: "invoiced",
                hover: "hover:border-teal-400",
                ring: "ring-teal-300",
                title: "View the invoiced book (billed history, per-invoice detail)",
              },
            };
            const cfg = BOOK_STAGES[s.key];
            const Tag = cfg ? "button" : "div";
            const isActive = cfg && activeBook === cfg.book;
            return (
              <Tag
                key={s.key}
                type={cfg ? "button" : undefined}
                onClick={cfg ? () => onSelectBook?.(cfg.book) : undefined}
                data-testid={`pipeline-mini-stage-${s.key}`}
                title={
                  cfg
                    ? cfg.title
                    : `${s.opportunity_count.toLocaleString()} ${s.label} deal${s.opportunity_count === 1 ? "" : "s"}`
                }
                className={`inline-flex flex-col rounded-lg border px-3 py-1.5 text-left ${STAGE_TONE[s.key] || "bg-stone-50 border-stone-200 text-stone-700"} ${cfg ? `cursor-pointer ${cfg.hover} hover:shadow-sm transition-all` : ""} ${isActive ? `ring-2 ring-offset-1 ${cfg.ring}` : ""}`}
              >
                <span className="text-[10px] font-mono uppercase tracking-wide opacity-75">
                  {s.label}
                  {cfg && <span className="ml-1 normal-case">↓</span>}
                </span>
                <span className="text-sm font-medium tabular-nums">
                  {s.opportunity_count.toLocaleString()}
                  <span className="text-[10px] font-mono ml-1.5 opacity-60">
                    {fmtCurrencyShort(s.total_value)}
                  </span>
                </span>
              </Tag>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Kpi({ label, value, sublabel, tone = "slate", testid, onClick, tooltip }) {
  const valueClass = {
    emerald: "text-emerald-700",
    rose: "text-rose-700",
    stone: "text-stone-700",
    navy: "text-[#1B4F8A]",
  }[tone] || "text-slate-900";
  const interactive = typeof onClick === "function";
  const Tag = interactive ? "button" : "div";
  const body = (
    <Tag
      type={interactive ? "button" : undefined}
      onClick={onClick}
      className={`bg-white px-4 py-3 text-left ${interactive ? "transition-colors hover:bg-stone-50 cursor-pointer" : ""} ${tooltip ? "hover:bg-stone-50 transition-colors" : ""}`}
      data-testid={testid}
    >
      <p className="text-[10px] font-mono uppercase tracking-wider text-stone-500 mb-1">
        {label}
      </p>
      <p className={`text-2xl font-medium tabular-nums leading-none ${valueClass}`}>
        {value}
      </p>
      {sublabel && (
        <p className="text-[10px] font-mono text-stone-400 mt-1.5">{sublabel}</p>
      )}
    </Tag>
  );
  if (!tooltip) return body;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{body}</TooltipTrigger>
      <TooltipContent
        side="bottom"
        className="max-w-xs bg-slate-900 text-stone-100 text-xs leading-relaxed px-3 py-2"
        data-testid={`${testid}-tooltip`}
      >
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}


